import React from 'react';
import { Insight } from '@/api/insightsApi';
import { chartColors } from './chartColors';
import { useAppTheme } from '@/contexts/AppThemeContext';

interface DefaultImpactVisualizationProps {
  insight: Insight;
}

export const DefaultImpactVisualization: React.FC<DefaultImpactVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const activitiesAffected = insight.activitiesAffected || 0;
  const milestonesAffected = insight.milestonesAffected || 0;
  const finishDelta = insight.finishDelta;
  const confidence = insight.confidence;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Activities Affected</div>
          <div className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>{activitiesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Milestones Affected</div>
          <div className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>{milestonesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Schedule Impact</div>
          <div className={`text-2xl font-bold ${
            finishDelta && finishDelta < 0 ? 'text-red-400' : 
            finishDelta && finishDelta > 0 ? 'text-green-400' : 
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {finishDelta !== undefined && finishDelta !== null
              ? `${finishDelta >= 0 ? '+' : ''}${finishDelta.toFixed(1)} days`
              : 'N/A'}
          </div>
        </div>
      </div>

      {/* Priority and Confidence */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h4 className={`text-sm font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Priority</h4>
          <div className="flex items-center justify-center h-24">
            <div className={`text-4xl font-bold ${
              insight.priority === 'High' ? 'text-red-400' :
              insight.priority === 'Medium' ? 'text-yellow-400' :
              'text-green-400'
            }`}>
              {insight.priority}
            </div>
          </div>
        </div>
        {confidence !== undefined && (
          <div className={`rounded-lg p-4 border ${
            theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h4 className={`text-sm font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Confidence</h4>
            <div className="flex items-center justify-center h-24">
              <div className="relative w-20 h-20">
                <svg className="transform -rotate-90 w-20 h-20">
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    stroke={chartColors.grid}
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    stroke={confidence >= 0.8 ? chartColors.success : confidence >= 0.6 ? chartColors.warning : chartColors.danger}
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 36}`}
                    strokeDashoffset={`${2 * Math.PI * 36 * (1 - confidence)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-lg font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {Math.round(confidence * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



